// src/components/FarmMark/FarmMarkFilterSheet.js
//
// Reuses the FarmPulse FilterSheet interaction pattern: a bottom sheet on
// mobile and a centered panel on desktop, with explicit Apply / Reset.
// Owns only FarmMark leaderboard dimensions (time period + category).

import React from "react";
import { X, RotateCcw, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TIME_RANGES } from "../../constants/farmMarkConstants";
import { IMPACT_LEADERBOARD_CATEGORIES } from "../../services/impactPointsService";

const OVERALL_LEADERBOARD_KEY = "overall";

const CATEGORY_OPTIONS = [
  { key: OVERALL_LEADERBOARD_KEY, labelKey: "leaderboard.categories.overall" },
  { key: IMPACT_LEADERBOARD_CATEGORIES.TOP_GROWERS, labelKey: "leaderboard.categories.topGrowers" },
  { key: IMPACT_LEADERBOARD_CATEGORIES.PRICE_SCOUTS, labelKey: "leaderboard.categories.priceScouts" },
  { key: IMPACT_LEADERBOARD_CATEGORIES.FIXERS, labelKey: "leaderboard.categories.fixers" },
];

const TIME_OPTIONS = [
  { value: TIME_RANGES.WEEK, labelKey: "leaderboard.timeRange.week" },
  { value: TIME_RANGES.MONTH, labelKey: "leaderboard.timeRange.month" },
  { value: TIME_RANGES.ALL_TIME, labelKey: "leaderboard.timeRange.allTime" },
];

export default function FarmMarkFilterSheet({
  open,
  onClose,
  onApply,
  onReset,
  draft,
  setDraft,
  t,
}) {
  if (!open) return null;

  const fieldClass =
    "w-full rounded-lg border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="farmmark-filter-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label={t("filters.close", { defaultValue: "Close filters" })}
      />
      <div className="relative w-full max-h-[90vh] overflow-y-auto sm:max-w-xl sm:rounded-2xl rounded-t-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
          <h2 id="farmmark-filter-title" className="text-base font-semibold text-gray-900 dark:text-white">
            {t("filters.title", { defaultValue: "Filter leaderboard" })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            aria-label={t("filters.close", { defaultValue: "Close filters" })}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {t("leaderboard.category", { defaultValue: "Category" })}
            <select
              className={fieldClass}
              value={draft.category}
              onChange={(e) => setDraft((v) => ({ ...v, category: e.target.value }))}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {t(opt.labelKey, { defaultValue: opt.key })}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {t("leaderboard.timeRange.title", { defaultValue: "Time period" })}
            <select
              className={fieldClass}
              value={draft.timeRange === null ? "null" : String(draft.timeRange)}
              onChange={(e) =>
                setDraft((v) => ({
                  ...v,
                  timeRange: e.target.value === "null" ? null : Number(e.target.value),
                }))
              }
            >
              {TIME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value === null ? "null" : String(opt.value)}>
                  {t(opt.labelKey, { defaultValue: opt.labelKey })}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 dark:border-neutral-800 p-4">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            {t("filters.reset", { defaultValue: "Reset" })}
          </button>
          <button
            type="button"
            onClick={onApply}
            className="ui-btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            {t("filters.apply", { defaultValue: "Apply" })}
          </button>
        </div>
      </div>
    </div>
  );
}